import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { BreadcrumbService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { finalize } from 'rxjs';
import _ from 'lodash';
import {
  ExpenseClaim,
  ExpenseClaimJournalLine,
  ExpenseClaimJournalPreview,
  ExpenseClaimStatus,
} from '../../../models/expense-claim/expense-claim';
import { ExpenseConcept } from '../../../models/expense-concept/expense-concept';
import { ExpenseClaimFormModel, expenseClaimFieldLabels } from '../../../models/expense-claim/expense-claim-form';
import {
  ExpenseReceipt,
  ExpenseReceiptOwner,
  ExpenseReceiptStatus,
  OwnerReference,
  ReceiptOwnerCode,
} from '../../../models/expense-receipt/expense-receipt';
import { ApprovalDecisionType, ApprovalSlot, ApproverType } from '../../../models/trip/trip';
import { ExpenseClaimProxyService } from '../../../proxies/expense-claim.proxy.service';
import { ExpenseConceptProxyService } from '../../../proxies/expense-concept.proxy.service';
import { ExpenseReceiptProxyService } from '../../../proxies/expense-receipt.proxy.service';
import { Currency, currencyLabel, currencySymbol } from '../../../utilities/currency.util';
import { SettlementSummaryRow } from '../../shared/settlement-summary/settlement-summary.component';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ControlsOf } from '../../../utilities/form.types';
import { EXPENSE_CLAIM_STATUSES, expenseClaimStatusLabel, expenseClaimStatusSeverity } from '../expense-claim-status.util';
import { ReceiptEditorSave } from '../expense-receipt-editor/expense-receipt-editor.component';

const LIST_ROUTE = '/expense-claims';
const NEW_ID = 'new';

/** One approver seat as the approval panel shows it. */
interface SlotView {
  slot: ApprovalSlot;
  state: 'approved' | 'rejected' | 'pending';
  by: string | null;
  at: string | null;
  comment: string | null;
}

/** The lifecycle strip at the top of the page. */
interface StepView {
  status: ExpenseClaimStatus;
  label: string;
  done: boolean;
  current: boolean;
}

interface OwnerOption {
  label: string;
  value: string;
  owner: ExpenseReceiptOwner;
}

/** What the decision dialog is about: the whole claim, or one receipt being rejected. */
type DecisionMode = 'approve' | 'reject' | 'rejectReceipt' | 'sendBack';

/**
 * One expense claim (Rendición de gastos, #5800). A fund's custodian, or a trip's employee or a TripAdmin, opens it
 * for a fund or trip and adds receipts from its pool; submitting freezes the owner's claim policy approvers. While in
 * approval an approver can take a receipt out (back to the pool), reject it (back to the capturer), approve the
 * claim (every receipt left) or reject it (back to Draft). Nothing posts here: Accounting review comes next (#5798).
 */
@Component({
  selector: 'app-expense-claim-details',
  templateUrl: './expense-claim-details.component.html',
})
export class ExpenseClaimDetailsComponent implements OnInit {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly proxy = inject(ExpenseClaimProxyService);
  private readonly receiptProxy = inject(ExpenseReceiptProxyService);
  private readonly conceptProxy = inject(ExpenseConceptProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly fieldLabels = expenseClaimFieldLabels;
  readonly ownerCodes = ReceiptOwnerCode;

  readonly isLoading = signal(0);
  readonly isNew = signal(true);
  readonly currentId = signal<string | null>(null);
  readonly claim = signal<ExpenseClaim | null>(null);
  readonly ready = signal(false);
  readonly owners = signal<ExpenseReceiptOwner[]>([]);
  /** The owner's In-pool receipts not yet in the claim. */
  readonly pool = signal<ExpenseReceipt[]>([]);
  readonly selectedPool = signal<ExpenseReceipt[]>([]);
  readonly ownerKey = signal<string | null>(null);

  readonly decisionVisible = signal(false);
  readonly decisionMode = signal<DecisionMode>('approve');
  readonly decisionReceipt = signal<ExpenseReceipt | null>(null);
  readonly decisionComment = new FormControl<string>('', { nonNullable: true });

  /** Accounting review (#5798): the receipt being reviewed, the concepts its lines pick from, and the entry preview. */
  readonly concepts = signal<ExpenseConcept[]>([]);
  readonly reviewingId = signal<string | null>(null);
  readonly reviewKey = signal(0);
  readonly preview = signal<ExpenseClaimJournalPreview | null>(null);
  readonly postVisible = signal(false);
  readonly postingDate = new FormControl<Date | null>(new Date(), Validators.required);

  readonly saveMenuItems: MenuItem[] = [
    { label: this.label('Save and Close', 'Save and Close'), icon: 'pi pi-save', command: () => this.save(true) },
  ];

  readonly form = new FormGroup<ControlsOf<ExpenseClaimFormModel>>({
    ownerKey: new FormControl<string | null>(null, Validators.required),
    description: new FormControl('', { nonNullable: true }),
  });

  readonly status = computed(() => this.claim()?.status ?? ExpenseClaimStatus.Draft);
  readonly canEdit = computed(() => this.isNew() || this.claim()?.canEdit === true);
  readonly canDecide = computed(() => this.claim()?.canDecide === true);
  readonly isDraft = computed(() => this.status() === ExpenseClaimStatus.Draft);
  readonly receipts = computed(() => this.claim()?.receiptDetails ?? []);
  readonly canSubmit = computed(() => !this.isNew() && this.canEdit() && this.receipts().length > 0);
  readonly canDelete = computed(() => !this.isNew() && this.canEdit());
  readonly canReview = computed(() => this.claim()?.canReview === true);
  readonly canRemove = computed(() => (this.canEdit() && this.isDraft()) || this.canDecide() || this.canReview());
  readonly canRejectReceipt = computed(() => this.canDecide() || this.canReview());
  readonly reviewingReceipt = computed(() => this.receipts().find((r) => r.id === this.reviewingId()) ?? null);
  readonly showPreview = computed(() => this.status() === ExpenseClaimStatus.AccountingReview && !_.isNil(this.preview()));
  readonly isPosted = computed(() => !_.isNil(this.claim()?.journalEntryReference));

  /**
   * The claim summary (right rail): subtotal before ISV, recoverable ISV, ISV that stays in the expense (vendor
   * without RTN), in the claim's currency — a receipt in another currency scales by its own converted total.
   */
  readonly summary = computed(() => {
    const claim = this.claim();
    const byId = new Map((claim?.receipts ?? []).map((r) => [r.reference, r]));
    let subtotal = 0;
    let recoverable = 0;
    let inExpense = 0;
    for (const receipt of this.receipts()) {
      const snapshot = byId.get(receipt.id);
      const factor = _.isNil(snapshot) || receipt.total === 0 ? 1 : snapshot.claimTotal / receipt.total;
      subtotal += receipt.subtotal * factor;
      if (receipt.isTaxRecoverable) recoverable += receipt.taxTotal * factor;
      else inExpense += receipt.taxTotal * factor;
    }
    const round = (value: number): number => Math.round(value * 100) / 100;
    return { subtotal: round(subtotal), recoverable: round(recoverable), inExpense: round(inExpense) };
  });

  readonly summaryRows = computed<SettlementSummaryRow[]>(() => {
    const rows: SettlementSummaryRow[] = [];
    const { recoverable, inExpense } = this.summary();
    if (recoverable > 0) rows.push({ label: this.label('ExpenseClaim_Summary_IsvRecoverable', 'ISV recoverable'), value: recoverable });
    if (inExpense > 0)
      rows.push({ label: this.label('ExpenseClaim_Summary_IsvInExpense', 'ISV in the expense (no RTN)'), value: inExpense, tone: 'muted' });
    return rows;
  });

  readonly summaryHint = computed(() => {
    const claim = this.claim();
    if (_.isNil(claim)) return '';
    return `${claim.receipts.length} ${this.label('ExpenseClaim_Summary_Receipts', 'receipts')} · ${this.label('ExpenseClaim_Summary_Usd', 'USD total for the approval policy')}: $ ${claim.totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  });

  /** A fund's total is what the custodian is replenished; a trip's what the company owes the employee. */
  readonly summaryNetLabel = computed(() =>
    this.claim()?.owner.entityDefinitionCode === ReceiptOwnerCode.Trip
      ? this.label('ExpenseClaim_Summary_Total_Trip', 'Total owed to the employee')
      : this.label('ExpenseClaim_Summary_Total_Fund', 'Total to replenish'),
  );
  readonly showPool = computed(() => this.canEdit() && this.isDraft() && !_.isNil(this.ownerKey()));
  readonly isRejection = computed(() => this.decisionMode() !== 'approve');

  readonly headerTitle = computed(() =>
    this.isNew() ? this.label('ExpenseClaim_New_Title', 'New expense claim') : (this.claim()?.number ?? ''),
  );

  readonly ownerOptions = computed<OwnerOption[]>(() =>
    this.owners().map((owner) => ({ label: `${owner.name} · ${owner.owner.number}`, value: this.keyOf(owner.owner), owner })),
  );

  /** The claim's currency: the fund's, or for a trip the company's (the API decides; a new trip claim shows the receipts' own). */
  readonly currency = computed<Currency | null>(() => {
    const claim = this.claim();
    if (!_.isNil(claim)) return claim.currency;
    return this.ownerOptions().find((o) => o.value === this.ownerKey())?.owner.currency ?? null;
  });

  readonly selectedTotalUsd = computed(() => _.sumBy(this.selectedPool(), (r) => r.totalUsd ?? 0));

  /** Shown on a Draft that came back from approval: who rejected it and why. */
  readonly lastRejection = computed(() => {
    const claim = this.claim();
    if (_.isNil(claim) || claim.status !== ExpenseClaimStatus.Draft) return null;
    const last = _.last(claim.statusHistory ?? []);
    return _.isNil(last?.comment) ? null : { by: last.user?.username ?? '', comment: last.comment };
  });

  readonly steps = computed<StepView[]>(() => {
    const index = EXPENSE_CLAIM_STATUSES.indexOf(this.status());
    return EXPENSE_CLAIM_STATUSES.map((step, i) => ({
      status: step,
      label: this.statusLabel(step),
      done: i < index,
      current: i === index,
    }));
  });

  readonly slotViews = computed<SlotView[]>(() => {
    const approval = this.claim()?.approval;
    if (_.isNil(approval)) return [];
    return _.sortBy(approval.slots, (s) => s.order ?? Number.MAX_SAFE_INTEGER).map((slot) => {
      const decision = approval.decisions.find((d) => d.slotId === slot.id) ?? null;
      return {
        slot,
        state: _.isNil(decision) ? 'pending' : decision.decision === ApprovalDecisionType.Approved ? 'approved' : 'rejected',
        by: decision?.username ?? null,
        at: decision?.at ?? null,
        comment: decision?.comment ?? null,
      };
    });
  });

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const id = params.get('id');
    if (_.isNil(id)) {
      this.back();
      return;
    }
    this.isNew.set(id === NEW_ID);
    this.currentId.set(id === NEW_ID ? null : id);

    this.form.controls.ownerKey.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((key) => {
      this.ownerKey.set(key);
      this.selectedPool.set([]);
      this.loadPool();
    });

    if (this.isNew()) {
      this.breadcrumbService.setCurrent('Common_New');
      const ownerReference = params.get('ownerReference');
      const ownerCode = params.get('ownerCode');
      this.loadOwners(_.isNil(ownerReference) || _.isNil(ownerCode) ? null : `${ownerReference}|${ownerCode}`);
      this.ready.set(true);
    } else {
      this.form.controls.ownerKey.disable({ emitEvent: false });
      this.loadItem(id);
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadOwners(preselect: string | null): void {
    this.startLoading();
    this.proxy
      .getOwners()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const owners = response.data ?? [];
        this.owners.set(owners);
        const key = owners.some((o) => this.keyOf(o.owner) === preselect)
          ? preselect
          : owners.length === 1
            ? this.keyOf(owners[0].owner)
            : null;
        if (!_.isNil(key)) this.form.controls.ownerKey.setValue(key);
      });
  }

  private loadItem(id: string): void {
    this.startLoading();
    this.proxy
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const claim = response.data;
        if (!_.isNil(claim)) this.setClaim(claim);
      });
  }

  private setClaim(claim: ExpenseClaim): void {
    this.claim.set(claim);
    this.breadcrumbService.setCurrent(claim.number);
    const key = this.keyOf(claim.owner);
    this.form.patchValue({ ownerKey: key, description: claim.description ?? '' }, { emitEvent: false });
    this.form.controls.ownerKey.disable({ emitEvent: false });
    if (claim.canEdit) {
      this.form.controls.description.enable({ emitEvent: false });
    } else {
      this.form.controls.description.disable({ emitEvent: false });
    }
    this.form.markAsPristine();
    this.ownerKey.set(key);
    this.selectedPool.set([]);
    this.loadPool();
    this.loadReview(claim);
    this.ready.set(true);
  }

  /** In Accounting review: the entry preview for everyone who can read the claim, the concepts for a reviewer. */
  private loadReview(claim: ExpenseClaim): void {
    if (claim.status !== ExpenseClaimStatus.AccountingReview) {
      this.preview.set(null);
      this.reviewingId.set(null);
      return;
    }
    if (claim.canReview && this.concepts().length === 0) {
      this.conceptProxy
        .search({ isActive: true }, 1, 200, 'code', 1)
        .subscribe((response) => this.concepts.set(response.data?.items ?? []));
    }
    this.startLoading();
    this.proxy
      .getJournalPreview(claim.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => this.preview.set(response.data ?? null));
  }

  openReview(receipt: ExpenseReceipt): void {
    this.reviewingId.set(receipt.id);
    this.reviewKey.update((k) => k + 1);
  }

  closeReview(): void {
    this.reviewingId.set(null);
  }

  saveReview(event: ReceiptEditorSave): void {
    const id = this.currentId();
    const receiptId = this.reviewingId();
    if (_.isNil(id) || _.isNil(receiptId)) return;
    this.startLoading();
    this.proxy
      .reviewReceipt(id, receiptId, event.update)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        GlobalMessageUtil.success(
          this.label('ExpenseClaim_Title', 'Expense claims'),
          this.label('ExpenseClaim_ReceiptReviewed', 'Receipt reviewed.'),
        );
        this.reviewingId.set(null);
        this.setClaim(response.data);
      });
  }

  openPost(): void {
    this.postingDate.reset(new Date());
    this.postVisible.set(true);
  }

  confirmPost(): void {
    const id = this.currentId();
    const date = this.postingDate.value;
    if (_.isNil(id) || _.isNil(date)) {
      this.postingDate.markAsTouched();
      return;
    }
    this.startLoading();
    this.proxy
      .post(id, this.toApiDate(date))
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        this.postVisible.set(false);
        GlobalMessageUtil.success(
          this.label('ExpenseClaim_Title', 'Expense claims'),
          `${this.label('ExpenseClaim_Posted', 'Expense claim posted:')} ${response.data.journalEntryNumber ?? ''}`,
        );
        this.setClaim(response.data);
      });
  }

  openSendBack(): void {
    this.openDecision('sendBack', null);
  }

  /** yyyy-MM-dd of the picked local day, so the server posts on that calendar day. */
  private toApiDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** The owner's In-pool receipts, for adding to a draft the caller can edit. */
  private loadPool(): void {
    const owner = this.ownerReference();
    if (_.isNil(owner) || !this.canEdit() || !this.isDraft()) {
      this.pool.set([]);
      return;
    }
    this.startLoading();
    this.receiptProxy
      .getPool(owner)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => this.pool.set((response.data?.receipts ?? []).filter((r) => r.status === ExpenseReceiptStatus.InPool)));
  }

  /** New: creates the claim with the selected receipts. Existing draft: saves the description. */
  save(shouldClose = false): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }
    const description = this.form.controls.description.value.trim() || null;
    const id = this.currentId();
    if (_.isNil(id)) {
      const owner = this.ownerReference();
      if (_.isNil(owner)) return;
      this.startLoading();
      this.proxy
        .create({ owner, description, receiptIds: this.selectedPool().map((r) => r.id) })
        .pipe(finalize(() => this.stopLoading()))
        .subscribe((response) => {
          const created = response.data;
          if (_.isNil(created)) return;
          GlobalMessageUtil.success(
            this.label('ExpenseClaim_Title', 'Expense claims'),
            this.label('ExpenseClaim_Created', 'Expense claim created.'),
          );
          if (shouldClose) {
            this.back();
            return;
          }
          this.isNew.set(false);
          this.currentId.set(created.id);
          this.router.navigate([], { queryParams: { id: created.id }, replaceUrl: true });
          this.setClaim(created);
        });
      return;
    }
    this.startLoading();
    this.proxy
      .update(id, { description })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        GlobalMessageUtil.success(
          this.label('ExpenseClaim_Title', 'Expense claims'),
          this.label('ExpenseClaim_Saved', 'Expense claim saved.'),
        );
        if (shouldClose) {
          this.back();
          return;
        }
        this.setClaim(response.data);
      });
  }

  addSelected(): void {
    const id = this.currentId();
    const ids = this.selectedPool().map((r) => r.id);
    if (_.isNil(id) || ids.length === 0) return;
    this.startLoading();
    this.proxy
      .addReceipts(id, ids)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        GlobalMessageUtil.success(
          this.label('ExpenseClaim_Title', 'Expense claims'),
          this.label('ExpenseClaim_ReceiptsAdded', 'Receipts added.'),
        );
        this.setClaim(response.data);
      });
  }

  removeReceipt(receipt: ExpenseReceipt): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.startLoading();
    this.proxy
      .removeReceipt(id, receipt.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        GlobalMessageUtil.success(
          this.label('ExpenseClaim_Title', 'Expense claims'),
          this.label('ExpenseClaim_ReceiptRemoved', 'Receipt back in the pool.'),
        );
        this.setClaim(response.data);
      });
  }

  /** Saves a changed description first, then sends the claim to its approvers. */
  submit(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.confirmationService.confirm({
      header: this.label('ExpenseClaim_Submit_Title', 'Send for approval'),
      message: this.label(
        'ExpenseClaim_Submit_Message',
        'Send this claim to its approvers? Receipts can no longer be added; approvers can still take them out.',
      ),
      icon: 'pi pi-send',
      acceptLabel: this.label('ExpenseClaim_Submit', 'Send for approval'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .submit(id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe((response) => {
            const submitted = response.data;
            if (_.isNil(submitted)) return;
            GlobalMessageUtil.success(
              this.label('ExpenseClaim_Title', 'Expense claims'),
              submitted.status === ExpenseClaimStatus.AccountingReview
                ? this.label('ExpenseClaim_Submitted_Auto', 'No approval needed: the claim went to Accounting review.')
                : this.label('ExpenseClaim_Submitted', 'Expense claim sent for approval.'),
            );
            this.setClaim(submitted);
          });
      },
    });
  }

  openApprove(): void {
    this.openDecision('approve', null);
  }

  openReject(): void {
    this.openDecision('reject', null);
  }

  openRejectReceipt(receipt: ExpenseReceipt): void {
    this.openDecision('rejectReceipt', receipt);
  }

  private openDecision(mode: DecisionMode, receipt: ExpenseReceipt | null): void {
    this.decisionMode.set(mode);
    this.decisionReceipt.set(receipt);
    this.decisionComment.reset('');
    this.decisionVisible.set(true);
  }

  confirmDecision(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    const comment = this.decisionComment.value.trim();
    if (this.isRejection() && comment === '') {
      this.decisionComment.markAsTouched();
      return;
    }
    const mode = this.decisionMode();
    const receipt = this.decisionReceipt();
    const request =
      mode === 'approve'
        ? this.proxy.approve(id, comment || null)
        : mode === 'reject'
          ? this.proxy.reject(id, comment)
          : mode === 'sendBack'
            ? this.proxy.sendBack(id, comment)
            : this.proxy.rejectReceipt(id, receipt?.id ?? '', comment);
    this.startLoading();
    request.pipe(finalize(() => this.stopLoading())).subscribe((response) => {
      const claim = response.data;
      if (_.isNil(claim)) return;
      this.decisionVisible.set(false);
      const message =
        mode === 'approve'
          ? this.label('ExpenseClaim_Approved', 'Approval recorded.')
          : mode === 'reject'
            ? this.label('ExpenseClaim_Rejected', 'Expense claim sent back to its draft.')
            : mode === 'sendBack'
              ? this.label('ExpenseClaim_SentBack', 'Expense claim sent back to approval.')
              : this.label('ExpenseClaim_ReceiptRejected', 'Receipt rejected back to its capturer.');
      GlobalMessageUtil.success(this.label('ExpenseClaim_Title', 'Expense claims'), message);
      this.setClaim(claim);
    });
  }

  confirmDelete(): void {
    const claim = this.claim();
    if (_.isNil(claim)) return;
    this.confirmationService.confirm({
      header: this.label('ExpenseClaim_Delete_Title', 'Delete expense claim'),
      message: this.label('ExpenseClaim_Delete_Message', 'Delete this draft? Its receipts go back to the pool.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('Delete', 'Delete'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .delete(claim.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe(() => {
            GlobalMessageUtil.success(
              this.label('ExpenseClaim_Title', 'Expense claims'),
              this.label('ExpenseClaim_Deleted', 'Expense claim deleted.'),
            );
            this.back();
          });
      },
    });
  }

  /** The fund or trip page this claim belongs to. */
  openOwner(): void {
    const owner = this.claim()?.owner;
    if (_.isNil(owner)) return;
    const route = owner.entityDefinitionCode === ReceiptOwnerCode.Trip ? '/trip-details' : '/petty-cash-fund-details';
    this.router.navigate([route], { queryParams: { id: owner.reference } });
  }

  back(): void {
    this.router.navigate([LIST_ROUTE]);
  }

  statusLabel(status: ExpenseClaimStatus): string {
    return expenseClaimStatusLabel(status, (k, f) => this.label(k, f));
  }

  statusSeverity(status: ExpenseClaimStatus): 'success' | 'secondary' | 'warn' | 'info' | 'danger' {
    return expenseClaimStatusSeverity(status);
  }

  policyName(name: { en: string; es: string }): string {
    return this.translate.currentLang === 'es' ? name.es : name.en;
  }

  slotIcon(slot: ApprovalSlot): string {
    return slot.type === ApproverType.Role ? 'pi pi-users' : 'pi pi-user';
  }

  symbolOf(currency: Currency | null | undefined): string {
    return currencySymbol(currency);
  }

  /** The receipt's concepts, for the tables. */
  conceptsOf(receipt: ExpenseReceipt): string {
    return _.uniq(receipt.lines.map((l) => l.concept?.name).filter((name): name is string => !_.isNil(name))).join(', ');
  }

  /** The receipt's total in the claim's currency, when it differs from its own. */
  claimTotalOf(receipt: ExpenseReceipt): number | null {
    const line = this.claim()?.receipts.find((r) => r.reference === receipt.id);
    return _.isNil(line) || line.currency === this.claim()?.currency ? null : line.claimTotal;
  }

  getJournalRow(rowData: unknown): ExpenseClaimJournalLine {
    return rowData as ExpenseClaimJournalLine;
  }

  currencyCode(): string {
    return currencyLabel(this.claim()?.currency);
  }

  getRow(rowData: unknown): ExpenseReceipt {
    return rowData as ExpenseReceipt;
  }

  onPoolSelection(selection: ExpenseReceipt[]): void {
    this.selectedPool.set(selection);
  }

  private ownerReference(): OwnerReference | null {
    const key = this.ownerKey();
    if (_.isNil(key)) return null;
    const [reference, code] = key.split('|');
    return { reference, entityDefinitionCode: Number(code) };
  }

  private keyOf(owner: { reference: string; entityDefinitionCode: number }): string {
    return `${owner.reference}|${owner.entityDefinitionCode}`;
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
